import { differenceInSeconds, format } from 'date-fns';
import RefreshIcon from 'assets/ibanera/refresh.svg';
import { OrderDetails, TradeCrypto } from './Crypto';
import { useEffect, useMemo, useState } from 'react';
import TFAForm from 'pages/signIn/TFAForm';
import { usePageTranslations } from 'helpers/usePageTranslations';
import { SignInTranslation } from 'pages/signIn/signInTypes';
import { PageResourceKey } from 'appConstants';
import { TFAField } from 'components/form/TFAField';
import { useTFAField } from 'helpers/useTFAField';
import Button from 'components/button/Button';
import { Form, useFormikContext } from 'formik';
import { roundToPrecision } from 'helpers/calculateToPrecision';
import { useSelector } from 'react-redux';
import { selectCurrencySymbols } from 'reducers/currencySymbols';
import { AssetCodeFormatter } from 'components/AssetCodeFormatter';
import { useTranslation } from 'react-i18next';

type ReviewOrderFormProps = {
    back: () => void;
    orderDetails: OrderDetails;
    refreshOrder: () => void;
    errorMessage: string;
    hideSubmitButton?: boolean;
    noFormComponent?: boolean;
};
export const ReviewOrderForm: React.FC<ReviewOrderFormProps> = ({
    back,
    orderDetails,
    refreshOrder,
    errorMessage,
    hideSubmitButton,
    noFormComponent,
}) => {
    const { t } = useTranslation('crypto');
    const [tfaType, toggleTfaType] = useTFAField();
    const { isSubmitting, submitForm, values, setFieldValue } = useFormikContext<TradeCrypto>();
    const currencySymbols = useSelector(selectCurrencySymbols);
    const pairedAssetSymbol = currencySymbols[orderDetails.pairedAsset];
    useEffect(() => {
        setFieldValue('tfaType', tfaType);
    }, [tfaType, setFieldValue]);

    const formContents = (
        <>
            <div className="LinkText notranslate" translate="no" onClick={back}>
                {t('back', { ns: 'common' })}
            </div>
            {/* <OrderTimer
    expiryDateString={orderDetails.validUntilUtcDate}
    refreshOrder={refreshOrder}
/> */}
            <h2 className="notranslate" translate="no">{`${orderDetails.assetExchangeType} ${t('orderSummary')}`}</h2>
            <div className="OrderSummaryContainer">
                <div className="SummarySection">
                    <p className="notranslate" translate="no">
                        {t('account')}{' '}
                        <span className="FloatRight">
                            <AssetCodeFormatter assetCode={orderDetails.asset} /> account
                        </span>
                    </p>
                </div>
                <div className="SummarySection">
                    <p className="notranslate" translate="no">
                        {t('amountOf')} <AssetCodeFormatter assetCode={orderDetails.asset} />{' '}
                        <span className="FloatRight">{orderDetails.amount}</span>
                    </p>
                    <p className="notranslate" translate="no">
                        {`${orderDetails.assetExchangeType === 'Buy' ? t('price') : t('value')} in ${
                            orderDetails.pairedAsset
                        }`}
                        <span className="FloatRight">{orderDetails.price}</span>
                    </p>
                </div>
                <div className="SummarySection">
                    <p className="notranslate" translate="no">
                        {t('networkFee')} <span className="FloatRight">{orderDetails.networkFee}</span>
                    </p>
                </div>
                <div className="SummarySection">
                    <p className="notranslate" translate="no">
                        {t('commission')} <span className="FloatRight">{orderDetails.commissionFee}</span>
                    </p>
                </div>
                <p>
                    <b>
                        {' '}
                        {orderDetails.assetExchangeType === 'Sell' ? t('totalPayout') : t('totalDue')}
                    </b>{' '}
                    <span className="FloatRight">
                        {`${pairedAssetSymbol ?? ''}${orderDetails.totalPrice} `}
                        <AssetCodeFormatter assetCode={orderDetails.pairedAsset} />
                    </span>
                </p>
                {orderDetails.assetExchangeType === 'Sell' && (
                    <p className="notranslate" translate="no">
                        <b>{t('transferType')}</b>{' '}
                        <span className="FloatRight">{values.transferType}</span>
                    </p>
                )}
            </div>
            <TFAField
                field={'tfaCode'}
                label={t('tfaCode')}
                required={true}
                toggleTFAType={
                    toggleTfaType
                        ? () => {
                              toggleTfaType();
                              setFieldValue(
                                  'tfaType',
                                  values.tfaType === 'SMS' ? 'AuthenticatorApp' : 'SMS'
                              );
                          }
                        : null
                }
                tfaType={tfaType}
                holdFocus
                autoFocus
            />
            {!hideSubmitButton && (
                <Button type="submit" variety="full" disabled={isSubmitting}>
                    {t('submit', { ns: 'common' })}
                </Button>
            )}
            {errorMessage && <div className="ErrorLabel">{errorMessage}</div>}
        </>
    );
    return noFormComponent ? (
        <div className="ContentBox LeftAlign">{formContents}</div>
    ) : (
        <Form className="ContentBox LeftAlign">{formContents}</Form>
    );
};
type OrderTimerProps = {
    expiryDateString: string;
    refreshOrder: () => void;
};
const OrderTimer: React.FC<OrderTimerProps> = ({ expiryDateString, refreshOrder }) => {
    const { t } = useTranslation('crypto');
    const expiryDate = useMemo(() => new Date(expiryDateString), [expiryDateString]);

    const calculateTimeLeft = (expiryDate: Date) => differenceInSeconds(expiryDate, Date.now());
    const [timeLeft, setTimeLeft] = useState<number>(calculateTimeLeft(expiryDate));
    useEffect(() => {
        if (timeLeft > 0) {
            setTimeout(() => setTimeLeft(calculateTimeLeft(expiryDate)), 1000);
        } else {
            const t = calculateTimeLeft(expiryDate);
            if (t > 0) setTimeLeft(t);
        }
    }, [timeLeft, expiryDate]);

    return (
        <div className="Refresh">
            <div>
                <p>
                    <span className="notranslate" translate="no">{t('currentTime')}</span>{' '}
                    <span>{format(new Date(), 'dd-MM-yyyy hh:mm:ss')}</span>
                </p>
                {timeLeft && timeLeft > 0 ? (
                    <p>
                        <span className="notranslate" translate="no">{t('validFor')}</span>
                        <span>{timeLeft} seconds</span>
                    </p>
                ) : (
                    <p className="ErrorLabel">{t('offerExpired')}</p>
                )}
            </div>
            <div className="RefreshBtn">
                <img src={RefreshIcon} alt="icon" onClick={refreshOrder} />
            </div>
        </div>
    );
};
